// @flow
import { HOME_FAMILIES } from './data';

type StoreType = {
  families: Array<Object>,
  evergreenArticles: Array<{
    tag: string,
    items: Array<Object>
  }>,
  products: Array<{
    tag: string,
    items: Array<Object>
  }>,
  questions: Array<{
    tag: string,
    items: Array<Object>
  }>,
  managers: Array<{
    tag: string,
    items: Array<Object>
  }>,
  managersContents: Array<{
    manager: string,
    items: Array<Object>
  }>,
  highlights: Array<Object>,
  // Los bloques de artículos de la home que edita el dashboard, por sección (Novedades, Eventos):
  // sus artículos en el orden de los huecos y su bloque (título y enlace). `fetched` solo lo pone una
  // carga que llegó: sin ella, la home no se da por cargada y el cliente la repite.
  sectionHighlights: { [string]: { items: Array<Object>, block: ?Object, fetched: boolean } },
  reviews: {
    items: Array<Object>,
    paging: Object
  },
  performanceCategories: Array<Object>,
  fetched: boolean
};

// Una entrada por tag (o por gestora): una carga nueva sustituye a la anterior. Antes se sumaban y quien
// lee (`find`) se quedaba con la primera, así que volver a cargar no refrescaba nada y el estado crecía.
const replaceEntry = (list: Array<Object>, entry: Object, isSame: Function): Array<Object> =>
  (list.some(isSame) ? list.map(item => (isSame(item) ? entry : item)) : [...list, entry]);

export function home(
  state: StoreType = {
    families: HOME_FAMILIES,
    evergreenArticles: [],
    products: [],
    questions: [],
    managers: [],
    managersContents: [],
    highlights: [],
    sectionHighlights: {},
    unMinuto: [],
    reviews: {
      items: [],
      authors: [],
      paging: {}
    },
    // Lo que leen de cada categoría los dos paneles de rentabilidades, el de producción y el
    // rediseño (performancePanel.js)
    performanceCategories: [],
    // El render de servidor dejó la home completa: no se vuelve a pedir al montarla. Se quita al
    // desmontarla (Home.js)
    fetched: false
  },
  action: Object
) {
  switch (action.type) {
    case 'SET_HOME_PERFORMANCE_CATEGORIES': {
      return {
        ...state,
        performanceCategories: [...action.categories]
      };
    }

    case 'SET_HOME_FETCHED': {
      return {
        ...state,
        fetched: action.fetched
      };
    }

    case 'SET_HOME_REVIEWS': {
      return {
        ...state,
        reviews: {
          items: [...action.reviews.reviews],
          authors: [...action.reviews.authors],
          paging: { ...action.paging }
        }
      };
    }

    case 'SET_HOME_HIGHLIGHTS': {
      return {
        ...state,
        highlights: [...action.highlights]
      };
    }

    case 'SET_HOME_SECTION_HIGHLIGHTS': {
      return {
        ...state,
        sectionHighlights: {
          ...state.sectionHighlights,
          [action.section]: { items: [...action.items], block: action.block || null, fetched: true }
        }
      };
    }

    case 'SET_HOME_UN_MINUTO': {
      return {
        ...state,
        unMinuto: [...action.items]
      };
    }

    case 'SET_HOME_TAG_EVERGREEN_ARTICLES': {
      return {
        ...state,
        evergreenArticles: replaceEntry(
          state.evergreenArticles,
          { tag: action.tag, items: action.items },
          item => item.tag.id === action.tag.id
        )
      };
    }

    case 'SET_HOME_TAG_PRODUCTS': {
      return {
        ...state,
        products: replaceEntry(
          state.products,
          { tag: action.tag, items: action.items },
          item => item.tag.id === action.tag.id
        )
      };
    }

    case 'SET_HOME_TAG_EVERGREEN_QUESTIONS': {
      return {
        ...state,
        questions: replaceEntry(
          state.questions,
          { tag: action.tag, items: action.items },
          item => item.tag.id === action.tag.id
        )
      };
    }

    case 'SET_HOME_TAG_EVERGREEN_MANAGERS': {
      return {
        ...state,
        managers: replaceEntry(
          state.managers,
          { tag: action.tag, items: action.items },
          item => item.tag.alias === action.tag.alias
        )
      };
    }

    case 'SET_HOME_TAG_EVERGREEN_MANAGER_CONTENTS': {
      return {
        ...state,
        managersContents: replaceEntry(
          state.managersContents,
          { manager: action.manager, items: action.items },
          item => item.manager === action.manager
        )
      };
    }

    default:
      return state;
  }
}
